import {
  BellIcon,
  ChartBarIcon,
  ChatsTeardropIcon,
  CompassIcon,
  MegaphoneIcon,
  ScrollIcon,
  SidebarSimpleIcon,
} from "@phosphor-icons/react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { HandraiseH, HandraiseLogoMark } from "@/assets/handraise";
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { useFeature } from "@/lib/feature-management";

const NAV_ITEMS = [
  { title: "Chat", icon: ChatsTeardropIcon, to: "/chat" },
  { title: "Discovery", icon: CompassIcon, to: "/discovery" },
  { title: "Analysis", icon: ChartBarIcon, to: "/analysis" },
  {
    title: "Dynamic Share of Voice",
    icon: MegaphoneIcon,
    to: "/share-of-voice",
  },
  { title: "News Alerts", icon: BellIcon, to: "/news-alerts" },
  {
    title: "Scheduled Briefings",
    icon: ScrollIcon,
    to: "/scheduled-briefings",
    flag: "enable-scheduled-briefings-v1",
  },
] as const;

const navigateToNewChat = (navigate: ReturnType<typeof useNavigate>) =>
  void navigate({
    to: "/chat/$conversationId",
    params: { conversationId: "new" },
  });

const LeftNavSideBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toggleSidebar } = useSidebar();

  const showScheduledBriefings = useFeature(
    "enable-scheduled-briefings-v1",
    false,
  );

  const navFeatureFlags = {
    "enable-scheduled-briefings-v1": showScheduledBriefings,
  } as const;

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !("flag" in item) || navFeatureFlags[item.flag],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Fixed height so separator stays on same plane as page header border (54px) when switching logos */}
      <div className="flex h-[54px] shrink-0 flex-col overflow-hidden">
        <SidebarHeader className="min-h-0 flex-1 justify-center py-0">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                size="lg"
                className="h-8! max-h-8! cursor-pointer p-0! transition-none! group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:justify-start group-data-[collapsible=icon]:pl-2! group-data-[state=expanded]:pl-2! hover:bg-transparent active:bg-transparent focus-visible:bg-transparent data-[state=open]:hover:bg-transparent group-data-[collapsible=icon]:hover:bg-transparent group-data-[collapsible=icon]:active:bg-transparent [&>svg]:group-data-[state=expanded]:h-4!"
                onClick={() => navigateToNewChat(navigate)}
              >
                <HandraiseH className="hidden size-4 shrink-0 group-data-[collapsible=icon]:block" />
                <HandraiseLogoMark className="h-4 w-auto shrink-0 group-data-[collapsible=icon]:hidden" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarSeparator
          orientation="horizontal"
          className="mx-2 mt-0 h-px w-auto shrink-0 bg-gray-100"
        />
      </div>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleNavItems.map((item) => {
                const isActive =
                  item.title === "Chat"
                    ? pathname.startsWith("/chat")
                    : pathname.startsWith(item.to);

                return (
                  <SidebarMenuItem
                    key={item.title}
                    className="flex h-8 items-center"
                  >
                    <SidebarMenuButton
                      tooltip={item.title}
                      isActive={isActive}
                      onClick={() => {
                        if (item.title === "Chat") {
                          navigateToNewChat(navigate);
                          return;
                        }
                        // biome-ignore lint/suspicious/noExplicitAny: temporarily bypass typed `to` routes during migration
                        navigate({ to: item.to as any });
                      }}
                    >
                      <item.icon className="size-4" />
                      <span className="text-xs">{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarSeparator
        orientation="horizontal"
        className="mx-2 my-0 h-px w-auto bg-gray-100"
      />

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Toggle sidebar"
              onClick={toggleSidebar}
              className="justify-start"
            >
              <SidebarSimpleIcon className="size-4" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail className="hover:after:bg-gray-100!" />
    </div>
  );
};

export default LeftNavSideBar;
